import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  detectFkFields,
  getSourceDictValues,
  saveProdFkConfig,
  getProdFkConfig,
  getProdTableList,
  getProdTableSchema,
  matchPersonRecords,
  matchDeptRecords,
  searchProdPersons,
  searchProdDepts,
  saveManualFkMatch,
  type DetectedFkField,
  type ProdFkConfigItem,
  type SaveFkConfigEntry,
  type FkManualMappingEntry,
  type ProdTableInfo,
  type ProdColumnInfo,
  type MatchResult,
  type ProdPersonItem,
  type ProdDeptItem,
} from '../../services/data-integration-api';

interface FkFieldConfig {
  sourceColumnName: string;
  prodColumnName: string;
}

interface ProdFkMappingPanelProps {
  normalizedTableId: string;
  writeMappingId: string;
  prodConnectionId: string;
  prodTableName: string;
  fkFields: FkFieldConfig[];
  onComplete: (mappingStatus: string, fkConfigStatus: string) => void;
}

interface ManualRow {
  source_value: string;
  count: number;
  prod_value: string;
  source_display: string;
  prod_display: string;
}

type ResolveMode = 'auto_lookup' | 'manual' | 'person_match' | 'dept_match';

interface FieldState {
  resolveMode: ResolveMode;
  // auto_lookup fields
  sourceRefTable: string;
  sourceRefPkColumn: string;
  sourceMatchColumn: string;
  prodRefTable: string;
  prodRefPkColumn: string;
  prodMatchColumn: string;
  // manual fields
  manualRows: ManualRow[];
  // detected info
  detected: DetectedFkField | null;
  // existing config id
  fkConfigId?: string;
  // person/dept match state
  matchResult: MatchResult | null;
  matchLoading: boolean;
  // manual designation for person/dept (key → selected prod record)
  manualDesignations: Record<string, { prodId: string; prodName: string }>;
}

export default function ProdFkMappingPanel({
  normalizedTableId, writeMappingId, prodConnectionId, prodTableName,
  fkFields, onComplete,
}: ProdFkMappingPanelProps) {
  const [activeField, setActiveField] = useState(0);
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldStatus, setFieldStatus] = useState<Record<string, boolean>>({});

  // 生产库引用表的列列表（用于选择匹配列）
  const [prodRefColumns, setProdRefColumns] = useState<ProdColumnInfo[]>([]);
  const [prodTables, setProdTables] = useState<ProdTableInfo[]>([]);

  const currentField = fkFields[activeField];

  // 初始化加载：检测 FK 字段 + 已有配置
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // 并行获取 FK 检测结果、已有配置、生产库表列表
        const [detected, existing, tables] = await Promise.all([
          detectFkFields({
            normalized_table_id: normalizedTableId,
            prod_connection_id: prodConnectionId,
            prod_table_name: prodTableName,
          }),
          getProdFkConfig({ write_mapping_id: writeMappingId }),
          getProdTableList({ prodConnectionId }),
        ]);

        setProdTables(tables.tables);

        // 按 source_column_name 索引已有配置
        const existingMap = new Map(
          existing.fk_configs.map(c => [c.source_column_name, c])
        );

        // 按 source_column_name 索引检测结果
        const detectedMap = new Map(
          detected.fk_fields.map(f => [f.source_column_name, f])
        );

        // 初始化每个 FK 字段的状态
        const states: Record<string, FieldState> = {};
        for (const f of fkFields) {
          const det = detectedMap.get(f.sourceColumnName) || null;
          const ext = existingMap.get(f.sourceColumnName);

          const baseState: FieldState = {
            resolveMode: 'auto_lookup',
            sourceRefTable: '',
            sourceRefPkColumn: '',
            sourceMatchColumn: '',
            prodRefTable: '',
            prodRefPkColumn: '',
            prodMatchColumn: '',
            manualRows: [],
            detected: det,
            matchResult: null,
            matchLoading: false,
            manualDesignations: {},
          };

          if (ext) {
            // 恢复已有配置
            states[f.sourceColumnName] = {
              ...baseState,
              resolveMode: ext.resolve_mode as ResolveMode,
              sourceRefTable: ext.source_ref_table,
              sourceRefPkColumn: ext.source_ref_pk_column,
              sourceMatchColumn: ext.source_match_column,
              prodRefTable: ext.prod_ref_table,
              prodRefPkColumn: ext.prod_ref_pk_column,
              prodMatchColumn: ext.prod_match_column,
              manualRows: ext.manual_mappings.map(m => ({
                source_value: m.source_value,
                count: 0,
                prod_value: m.prod_value,
                source_display: m.source_display || '',
                prod_display: m.prod_display || '',
              })),
              fkConfigId: ext._id,
            };
            setFieldStatus(prev => ({ ...prev, [f.sourceColumnName]: true }));
          } else {
            // 使用检测建议初始化
            states[f.sourceColumnName] = {
              ...baseState,
              sourceRefTable: det?.ref_table_name || '',
              sourceRefPkColumn: det?.ref_column_name || '',
              sourceMatchColumn: det?.suggested_match_columns?.[0] || '',
              prodRefTable: det?.prod_ref_table_name || '',
              prodRefPkColumn: det?.ref_column_name || '',
              prodMatchColumn: det?.suggested_match_columns?.[0] || '',
            };
          }
        }
        setFieldStates(states);
      } catch (e: any) {
        toast.error('加载 FK 配置失败: ' + e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [normalizedTableId, writeMappingId, prodConnectionId, prodTableName, fkFields]);

  // 当切换字段或生产引用表变化时，加载生产引用表列
  useEffect(() => {
    if (!currentField) return;
    const state = fieldStates[currentField.sourceColumnName];
    if (!state?.prodRefTable || state.resolveMode === 'person_match' || state.resolveMode === 'dept_match') {
      setProdRefColumns([]);
      return;
    }
    const loadColumns = async () => {
      try {
        const schema = await getProdTableSchema({
          prodConnectionId,
          tableName: state.prodRefTable,
        });
        setProdRefColumns(schema.columns);
      } catch {
        setProdRefColumns([]);
      }
    };
    loadColumns();
  }, [currentField, fieldStates, prodConnectionId]);

  // 加载手动映射所需的源值
  const loadManualValues = useCallback(async () => {
    if (!currentField) return;
    const state = fieldStates[currentField.sourceColumnName];
    if (!state || state.resolveMode !== 'manual') return;

    try {
      const result = await getSourceDictValues({
        normalizedTableId,
        columnName: currentField.sourceColumnName,
      });
      const existingMap = new Map(
        state.manualRows.map(r => [r.source_value, r])
      );
      const rows: ManualRow[] = result.values.map(v => {
        const existing = existingMap.get(v.value);
        return {
          source_value: v.value,
          count: v.count,
          prod_value: existing?.prod_value || '',
          source_display: existing?.source_display || '',
          prod_display: existing?.prod_display || '',
        };
      });
      updateFieldState(currentField.sourceColumnName, { manualRows: rows });
    } catch (e: any) {
      toast.error('加载源值失败: ' + e.message);
    }
  }, [currentField, fieldStates, normalizedTableId]);

  const updateFieldState = useCallback((colName: string, updates: Partial<FieldState>) => {
    setFieldStates(prev => ({
      ...prev,
      [colName]: { ...prev[colName], ...updates },
    }));
  }, []);

  const updateManualRow = useCallback((colName: string, index: number, updates: Partial<ManualRow>) => {
    setFieldStates(prev => {
      const state = prev[colName];
      const rows = [...state.manualRows];
      rows[index] = { ...rows[index], ...updates };
      return { ...prev, [colName]: { ...state, manualRows: rows } };
    });
  }, []);

  // 人员/部门自动匹配
  const handleAutoMatch = useCallback(async (colName: string, mode: 'person_match' | 'dept_match') => {
    updateFieldState(colName, { matchLoading: true });
    try {
      const result = mode === 'person_match'
        ? await matchPersonRecords({ writeMappingId, sourceColumnName: colName })
        : await matchDeptRecords({ writeMappingId, sourceColumnName: colName });

      updateFieldState(colName, {
        matchResult: result,
        matchLoading: false,
        manualDesignations: {},
      });

      if (result.unmatched === 0) {
        toast.success(`自动匹配完成: 全部 ${result.total} 条记录匹配成功`);
        setFieldStatus(prev => ({ ...prev, [colName]: true }));
        onComplete('ready', 'complete');
      } else {
        toast.info(`自动匹配完成: ${result.auto_matched}/${result.total} 条匹配，${result.unmatched} 条待手动标定`);
      }
    } catch (e: any) {
      toast.error('自动匹配失败: ' + e.message);
      updateFieldState(colName, { matchLoading: false });
    }
  }, [writeMappingId, updateFieldState, onComplete]);

  // 保存手动标定（人员/部门匹配模式）
  const handleSaveDesignations = useCallback(async () => {
    if (!currentField) return;
    const state = fieldStates[currentField.sourceColumnName];
    if (!state) return;

    const entries = Object.entries(state.manualDesignations);
    if (entries.length === 0) {
      toast.error('未选择任何标定');
      return;
    }

    setSaving(true);
    try {
      const fkType = state.resolveMode === 'person_match' ? 'person' as const : 'department' as const;
      const result = await saveManualFkMatch({
        writeMappingId,
        sourceColumnName: currentField.sourceColumnName,
        fkType,
        matches: entries.map(([key, val]) => ({
          source_value_key: key,
          prod_id: val.prodId,
          prod_display_name: val.prodName,
        })),
      });

      toast.success(`保存成功: ${result.updated} 条标定`);

      if (result.remaining_unmatched === 0) {
        setFieldStatus(prev => ({ ...prev, [currentField.sourceColumnName]: true }));
        onComplete('ready', result.fk_config_status);
      }

      // 刷新匹配结果
      handleAutoMatch(currentField.sourceColumnName, state.resolveMode as 'person_match' | 'dept_match');
    } catch (e: any) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [currentField, fieldStates, writeMappingId, onComplete, handleAutoMatch]);

  // 保存当前字段配置（auto_lookup / manual 模式）
  const handleSave = useCallback(async () => {
    if (!currentField) return;
    const state = fieldStates[currentField.sourceColumnName];
    if (!state) return;

    // 验证
    if (state.resolveMode === 'auto_lookup') {
      if (!state.sourceRefTable || !state.sourceMatchColumn || !state.prodRefTable || !state.prodMatchColumn) {
        toast.error('自动查找模式需要填写完整的源库和生产库引用表信息');
        return;
      }
    }

    setSaving(true);
    try {
      const cfg: SaveFkConfigEntry = {
        fk_config_id: state.fkConfigId,
        source_column_name: currentField.sourceColumnName,
        resolve_mode: state.resolveMode,
        source_ref_table: state.sourceRefTable,
        source_ref_pk_column: state.sourceRefPkColumn,
        source_match_column: state.sourceMatchColumn,
        prod_ref_table: state.prodRefTable,
        prod_ref_pk_column: state.prodRefPkColumn,
        prod_match_column: state.prodMatchColumn,
        manual_mappings: state.resolveMode === 'manual'
          ? state.manualRows.filter(r => r.prod_value).map(r => ({
              source_value: r.source_value,
              prod_value: r.prod_value,
              source_display: r.source_display,
              prod_display: r.prod_display,
            }))
          : undefined,
      };

      const result = await saveProdFkConfig({
        write_mapping_id: writeMappingId,
        fk_configs: [cfg],
      });

      // 更新 config id
      const savedCfg = result.saved_configs.find(c => c.source_column_name === currentField.sourceColumnName);
      if (savedCfg) {
        updateFieldState(currentField.sourceColumnName, { fkConfigId: savedCfg.fk_config_id });
      }

      setFieldStatus(prev => ({ ...prev, [currentField.sourceColumnName]: true }));
      toast.success('FK 配置保存成功');

      onComplete(result.mapping_status, result.fk_config_status);

      // 自动切换到下一个未配置的字段
      if (activeField < fkFields.length - 1) {
        setActiveField(activeField + 1);
      }
    } catch (e: any) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [currentField, fieldStates, writeMappingId, onComplete, activeField, fkFields.length, updateFieldState]);

  if (fkFields.length === 0) {
    return <p className="text-muted-foreground">无 FK 外键字段需要配置</p>;
  }

  if (loading) {
    return <p className="text-muted-foreground">加载 FK 检测信息...</p>;
  }

  const state = currentField ? fieldStates[currentField.sourceColumnName] : null;

  return (
    <div>
      <h4 className="text-sm font-medium mb-3">FK 外键映射配置 ({fkFields.length} 个 FK 字段)</h4>

      {/* 字段选择 tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {fkFields.map((f, i) => (
          <Button
            key={f.sourceColumnName}
            variant={i === activeField ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveField(i)}
          >
            {f.sourceColumnName}
            {fieldStatus[f.sourceColumnName] && <Badge variant="default" className="ml-1 text-[10px]">已配置</Badge>}
          </Button>
        ))}
      </div>

      {currentField && state && (
        <div className="space-y-4">
          {/* 检测信息 */}
          {state.detected && (
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded space-y-1">
              <p>引用表: <span className="font-mono">{state.detected.ref_table_name}</span> ({state.detected.ref_table_display})</p>
              <p>引用列: <span className="font-mono">{state.detected.ref_column_name}</span></p>
              <p>
                生产库引用表:
                {state.detected.prod_ref_table_exists
                  ? <Badge variant="default" className="ml-1 text-[10px]">{state.detected.prod_ref_table_name}</Badge>
                  : <Badge variant="destructive" className="ml-1 text-[10px]">不存在</Badge>
                }
              </p>
              {state.detected.suggested_match_columns.length > 0 && (
                <p>建议匹配列: {state.detected.suggested_match_columns.map(c => (
                  <Badge key={c} variant="secondary" className="ml-1 text-[10px]">{c}</Badge>
                ))}</p>
              )}
            </div>
          )}

          {/* 解析模式选择 */}
          <div className="flex items-center gap-4 flex-wrap">
            <label className="text-sm font-medium">解析模式:</label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name={`resolve-mode-${currentField.sourceColumnName}`}
                checked={state.resolveMode === 'person_match'}
                onChange={() => updateFieldState(currentField.sourceColumnName, { resolveMode: 'person_match' })}
                className="accent-primary"
              />
              人员匹配
            </label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name={`resolve-mode-${currentField.sourceColumnName}`}
                checked={state.resolveMode === 'dept_match'}
                onChange={() => updateFieldState(currentField.sourceColumnName, { resolveMode: 'dept_match' })}
                className="accent-primary"
              />
              部门匹配
            </label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name={`resolve-mode-${currentField.sourceColumnName}`}
                checked={state.resolveMode === 'auto_lookup'}
                onChange={() => updateFieldState(currentField.sourceColumnName, { resolveMode: 'auto_lookup' })}
                className="accent-primary"
              />
              自动查找
            </label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="radio"
                name={`resolve-mode-${currentField.sourceColumnName}`}
                checked={state.resolveMode === 'manual'}
                onChange={() => {
                  updateFieldState(currentField.sourceColumnName, { resolveMode: 'manual' });
                  setTimeout(loadManualValues, 0);
                }}
                className="accent-primary"
              />
              手动映射
            </label>
          </div>

          {/* 人员匹配模式 */}
          {state.resolveMode === 'person_match' && (
            <PersonMatchSection
              colName={currentField.sourceColumnName}
              state={state}
              onAutoMatch={() => handleAutoMatch(currentField.sourceColumnName, 'person_match')}
              onDesignate={(key, prodId, prodName) => {
                updateFieldState(currentField.sourceColumnName, {
                  manualDesignations: {
                    ...state.manualDesignations,
                    [key]: { prodId, prodName },
                  },
                });
              }}
              onSave={handleSaveDesignations}
              saving={saving}
            />
          )}

          {/* 部门匹配模式 */}
          {state.resolveMode === 'dept_match' && (
            <DeptMatchSection
              colName={currentField.sourceColumnName}
              state={state}
              onAutoMatch={() => handleAutoMatch(currentField.sourceColumnName, 'dept_match')}
              onDesignate={(key, prodId, prodName) => {
                updateFieldState(currentField.sourceColumnName, {
                  manualDesignations: {
                    ...state.manualDesignations,
                    [key]: { prodId, prodName },
                  },
                });
              }}
              onSave={handleSaveDesignations}
              saving={saving}
            />
          )}

          {/* 自动查找配置 */}
          {state.resolveMode === 'auto_lookup' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <h5 className="text-xs font-medium text-muted-foreground">源库引用表</h5>
                <div className="space-y-1.5">
                  <label className="text-xs">引用表名</label>
                  <Input
                    value={state.sourceRefTable}
                    onChange={e => updateFieldState(currentField.sourceColumnName, { sourceRefTable: e.target.value })}
                    placeholder="如: sys_user"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs">主键列</label>
                  <Input
                    value={state.sourceRefPkColumn}
                    onChange={e => updateFieldState(currentField.sourceColumnName, { sourceRefPkColumn: e.target.value })}
                    placeholder="如: id"
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs">匹配列 (业务唯一列)</label>
                  <Input
                    value={state.sourceMatchColumn}
                    onChange={e => updateFieldState(currentField.sourceColumnName, { sourceMatchColumn: e.target.value })}
                    placeholder="如: user_code"
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h5 className="text-xs font-medium text-muted-foreground">生产库引用表</h5>
                <div className="space-y-1.5">
                  <label className="text-xs">引用表名</label>
                  <select
                    value={state.prodRefTable}
                    onChange={e => updateFieldState(currentField.sourceColumnName, { prodRefTable: e.target.value })}
                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                  >
                    <option value="">-- 请选择 --</option>
                    {prodTables.map(t => (
                      <option key={t.table_name} value={t.table_name}>
                        {t.table_name}{t.table_comment ? ` (${t.table_comment})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs">主键列</label>
                  {prodRefColumns.length > 0 ? (
                    <select
                      value={state.prodRefPkColumn}
                      onChange={e => updateFieldState(currentField.sourceColumnName, { prodRefPkColumn: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                    >
                      <option value="">-- 请选择 --</option>
                      {prodRefColumns.map(c => (
                        <option key={c.column_name} value={c.column_name}>
                          {c.column_name} ({c.data_type}){c.is_primary_key ? ' [PK]' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={state.prodRefPkColumn}
                      onChange={e => updateFieldState(currentField.sourceColumnName, { prodRefPkColumn: e.target.value })}
                      placeholder="如: id"
                      className="h-8 text-xs"
                    />
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs">匹配列 (业务唯一列)</label>
                  {prodRefColumns.length > 0 ? (
                    <select
                      value={state.prodMatchColumn}
                      onChange={e => updateFieldState(currentField.sourceColumnName, { prodMatchColumn: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                    >
                      <option value="">-- 请选择 --</option>
                      {prodRefColumns.map(c => (
                        <option key={c.column_name} value={c.column_name}>
                          {c.column_name} ({c.data_type}){c.column_comment ? ` - ${c.column_comment}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={state.prodMatchColumn}
                      onChange={e => updateFieldState(currentField.sourceColumnName, { prodMatchColumn: e.target.value })}
                      placeholder="如: user_code"
                      className="h-8 text-xs"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* 手动映射表格 */}
          {state.resolveMode === 'manual' && (
            <div>
              {state.manualRows.length === 0 ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">点击下方按钮加载源库中该字段的所有去重值</p>
                  <Button variant="outline" size="sm" onClick={loadManualValues}>加载源值</Button>
                </div>
              ) : (
                <div className="border rounded-md overflow-auto max-h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[150px]">源 FK 值</TableHead>
                        <TableHead className="w-[80px]">记录数</TableHead>
                        <TableHead className="w-[200px]">生产库对应 ID</TableHead>
                        <TableHead className="w-[150px]">备注</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {state.manualRows.map((r, i) => (
                        <TableRow key={r.source_value} className={!r.prod_value ? 'bg-yellow-50' : ''}>
                          <TableCell className="font-mono text-xs">{r.source_value || '(空值)'}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.count}</TableCell>
                          <TableCell>
                            <Input
                              value={r.prod_value}
                              onChange={e => updateManualRow(currentField.sourceColumnName, i, { prod_value: e.target.value })}
                              placeholder="输入生产库 ID"
                              className="h-8 text-xs"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={r.prod_display}
                              onChange={e => updateManualRow(currentField.sourceColumnName, i, { prod_display: e.target.value })}
                              placeholder="备注（可选）"
                              className="h-8 text-xs"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* 保存按钮（auto_lookup / manual 模式） */}
          {(state.resolveMode === 'auto_lookup' || state.resolveMode === 'manual') && (
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? '保存中...' : '保存 FK 配置'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 人员匹配子组件 ─────────────────────────────────────────────

interface MatchSectionProps {
  colName: string;
  state: FieldState;
  onAutoMatch: () => void;
  onDesignate: (key: string, prodId: string, prodName: string) => void;
  onSave: () => void;
  saving: boolean;
}

function PersonMatchSection({ colName, state, onAutoMatch, onDesignate, onSave, saving }: MatchSectionProps) {
  return (
    <div className="space-y-4">
      {/* 自动匹配按钮 */}
      <div className="flex items-center gap-3">
        <Button onClick={onAutoMatch} disabled={state.matchLoading}>
          {state.matchLoading ? '匹配中...' : '执行人员自动匹配'}
        </Button>
        <span className="text-xs text-muted-foreground">
          按手机号 + 姓名精确匹配生产环境人员记录
        </span>
      </div>

      {/* 匹配统计 */}
      {state.matchResult && (
        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded text-sm">
          <span>总计: <strong>{state.matchResult.total}</strong></span>
          <span className="text-green-600">已匹配: <strong>{state.matchResult.auto_matched}</strong></span>
          <span className="text-orange-500">未匹配: <strong>{state.matchResult.unmatched}</strong></span>
        </div>
      )}

      {/* 未匹配列表 + 搜索标定 */}
      {state.matchResult && state.matchResult.unmatched_list.length > 0 && (
        <div>
          <h5 className="text-sm font-medium mb-2">未匹配记录（搜索选择生产环境人员）</h5>
          <div className="border rounded-md overflow-auto max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">姓名</TableHead>
                  <TableHead className="w-[130px]">手机号</TableHead>
                  <TableHead>搜索匹配生产环境人员</TableHead>
                  <TableHead className="w-[120px]">已选</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.matchResult.unmatched_list.map((item) => {
                  const key = `${item.source_person_name || ''}|${item.source_person_phone || ''}`;
                  const designation = state.manualDesignations[key];
                  return (
                    <TableRow key={key}>
                      <TableCell className="text-xs">{item.source_person_name || '-'}</TableCell>
                      <TableCell className="text-xs font-mono">{item.source_person_phone || '-'}</TableCell>
                      <TableCell>
                        <PersonSearchInput
                          onSelect={(person) => onDesignate(key, person.id, person.name)}
                        />
                      </TableCell>
                      <TableCell className="text-xs">
                        {designation ? (
                          <Badge variant="default" className="text-[10px]">{designation.prodName}</Badge>
                        ) : (
                          <span className="text-muted-foreground">未选择</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={onSave}
              disabled={saving || Object.keys(state.manualDesignations).length === 0}
            >
              {saving ? '保存中...' : `保存手动标定 (${Object.keys(state.manualDesignations).length} 条)`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 部门匹配子组件 ─────────────────────────────────────────────

function DeptMatchSection({ colName, state, onAutoMatch, onDesignate, onSave, saving }: MatchSectionProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button onClick={onAutoMatch} disabled={state.matchLoading}>
          {state.matchLoading ? '匹配中...' : '执行部门自动匹配'}
        </Button>
        <span className="text-xs text-muted-foreground">
          按名称关键字匹配生产环境部门记录
        </span>
      </div>

      {state.matchResult && (
        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded text-sm">
          <span>总计: <strong>{state.matchResult.total}</strong></span>
          <span className="text-green-600">已匹配: <strong>{state.matchResult.auto_matched}</strong></span>
          <span className="text-orange-500">未匹配: <strong>{state.matchResult.unmatched}</strong></span>
        </div>
      )}

      {state.matchResult && state.matchResult.unmatched_list.length > 0 && (
        <div>
          <h5 className="text-sm font-medium mb-2">未匹配记录（搜索选择生产环境部门）</h5>
          <div className="border rounded-md overflow-auto max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">部门名称</TableHead>
                  <TableHead>搜索匹配生产环境部门</TableHead>
                  <TableHead className="w-[150px]">已选</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.matchResult.unmatched_list.map((item) => {
                  const key = item.source_dept_name || '';
                  const designation = state.manualDesignations[key];
                  return (
                    <TableRow key={key}>
                      <TableCell className="text-xs">{item.source_dept_name || '-'}</TableCell>
                      <TableCell>
                        <DeptSearchInput
                          onSelect={(dept) => onDesignate(key, dept.id, dept.name)}
                        />
                      </TableCell>
                      <TableCell className="text-xs">
                        {designation ? (
                          <Badge variant="default" className="text-[10px]">{designation.prodName}</Badge>
                        ) : (
                          <span className="text-muted-foreground">未选择</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="flex gap-2 mt-3">
            <Button
              onClick={onSave}
              disabled={saving || Object.keys(state.manualDesignations).length === 0}
            >
              {saving ? '保存中...' : `保存手动标定 (${Object.keys(state.manualDesignations).length} 条)`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 搜索组件 ─────────────────────────────────────────────────

function PersonSearchInput({ onSelect }: { onSelect: (person: ProdPersonItem) => void }) {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<ProdPersonItem[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const doSearch = useCallback(async (kw: string) => {
    if (!kw.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    setSearching(true);
    try {
      const res = await searchProdPersons({ keyword: kw.trim(), pageSize: 10 });
      setResults(res.items);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleChange = (value: string) => {
    setKeyword(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(value), 400);
  };

  const handleSelect = (person: ProdPersonItem) => {
    onSelect(person);
    setKeyword(`${person.name} (${person.phone})`);
    setOpen(false);
  };

  // 点击外部关闭
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={keyword}
        onChange={e => handleChange(e.target.value)}
        placeholder="输入姓名或手机号搜索..."
        className="h-8 text-xs"
      />
      {searching && <span className="absolute right-2 top-1.5 text-[10px] text-muted-foreground">搜索中...</span>}
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-auto">
          {results.map(p => (
            <button
              key={p.id}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center justify-between"
              onClick={() => handleSelect(p)}
            >
              <span>{p.name}{p.nickName ? ` (${p.nickName})` : ''}</span>
              <span className="text-muted-foreground font-mono">{p.phone}</span>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && !searching && keyword.trim() && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-3 text-xs text-muted-foreground">
          无匹配结果
        </div>
      )}
    </div>
  );
}

function DeptSearchInput({ onSelect }: { onSelect: (dept: ProdDeptItem) => void }) {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<ProdDeptItem[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const doSearch = useCallback(async (kw: string) => {
    if (!kw.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    setSearching(true);
    try {
      const res = await searchProdDepts({ keyword: kw.trim(), pageSize: 10 });
      setResults(res.items);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleChange = (value: string) => {
    setKeyword(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(value), 400);
  };

  const handleSelect = (dept: ProdDeptItem) => {
    onSelect(dept);
    setKeyword(dept.name);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={keyword}
        onChange={e => handleChange(e.target.value)}
        placeholder="输入部门名称搜索..."
        className="h-8 text-xs"
      />
      {searching && <span className="absolute right-2 top-1.5 text-[10px] text-muted-foreground">搜索中...</span>}
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-auto">
          {results.map(d => (
            <button
              key={d.id}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center justify-between"
              onClick={() => handleSelect(d)}
            >
              <span>{d.name}</span>
              <span className="text-muted-foreground">{d.departmentCode}</span>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && !searching && keyword.trim() && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-3 text-xs text-muted-foreground">
          无匹配结果
        </div>
      )}
    </div>
  );
}
